import React from 'react';
import cn from 'classnames';
import { Collapse } from 'antd';
import type { TFormAnswer } from 'types/textVisit';
import type { TIntakeAssessmentNavItem } from 'utils/intakeAssessmentScores';
import AnswerLabel from '../AnswerLabel';
import s from './s.module.less';

const LEVEL_COLORS = ['#6EE7B7', '#FDE68A', '#FDBA74', '#F87171'] as const;

type IntakeScoreLevelBarProps = {
    value?: number;
    max?: number;
    getLevel: (num: number) => number;
};

/** Same behavior as former LevelBar; defined here so webpack/esbuild never drops the binding. */
function IntakeScoreLevelBar(props: IntakeScoreLevelBarProps) {
    const { value = 0, max = 10, getLevel } = props;
    const [color, setColor] = React.useState<string>();
    const getLevelRef = React.useRef(getLevel);
    getLevelRef.current = getLevel;

    React.useLayoutEffect(() => {
        const nextLevel = getLevelRef.current(value || 0);
        let nextColor: string | undefined;
        if (nextLevel === 0 || nextLevel === 1) nextColor = LEVEL_COLORS[0];
        else if (nextLevel === 2) nextColor = LEVEL_COLORS[1];
        else if (nextLevel === 3) nextColor = LEVEL_COLORS[2];
        else if (nextLevel === 4) nextColor = LEVEL_COLORS[3];
        setColor(nextColor);
    }, [value, max]);

    return (
        <div className={s.levelBarTrack}>
            <div
                className={s.levelBarFill}
                style={{ width: `${(value / max) * 100}%`, backgroundColor: color }}
            />
        </div>
    );
}

export type SpecificIntakeFormAssessmentLayout = 'drawer' | 'embedded';

export type SpecificIntakeFormAssessmentBlockProps = {
    layout: SpecificIntakeFormAssessmentLayout;
    navItems: TIntakeAssessmentNavItem[];
    groupedIntakeAnswers: Record<string, TFormAnswer[]>;
    /** Rendered between score cards and section collapse (e.g. common intake Q&A). */
    commonSection?: React.ReactNode;
};

const ArrowRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.2929 5.29289C12.6834 4.90237 13.3166 4.90237 13.7071 5.29289L17.7071 9.29289C18.0976 9.68342 18.0976 10.3166 17.7071 10.7071L13.7071 14.7071C13.3166 15.0976 12.6834 15.0976 12.2929 14.7071C11.9024 14.3166 11.9024 13.6834 12.2929 13.2929L14.5858 11H3C2.44772 11 2 10.5523 2 10C2 9.44772 2.44772 9 3 9H14.5858L12.2929 6.70711C11.9024 6.31658 11.9024 5.68342 12.2929 5.29289Z"
            fill="#111827"
        />
    </svg>
);

const ChevronDownIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
            d="M19 9L12 16L5 9"
            stroke="#111827"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Psychiatric / mental health intake score summary (nav cards + per-section collapse).
 * Used by IntakeFormDrawer and Prospects/VideoVisit so scoring UI stays identical.
 */
const SpecificIntakeFormAssessmentBlock = (props: SpecificIntakeFormAssessmentBlockProps) => {
    const { layout, navItems, groupedIntakeAnswers, commonSection } = props;
    const embedded = layout === 'embedded';
    const showNav = navItems.length > 0;

    if (!showNav && !commonSection) {
        return null;
    }

    return (
        <>
            {showNav && (
                <div className={cn(s.scoreCards, embedded && s.scoreCardsEmbedded)}>
                    {navItems.map((item) => (
                        <div key={item.name} className={s.scoreCard}>
                            <div className={s.scoreCardTitle}>
                                <span>{item.name}</span>
                                <ArrowRightIcon />
                            </div>
                            <div className={s.scoreCardValueRow}>
                                <div className={s.scoreCardNums}>
                                    <span className={s.scoreCardNumBold}>{item.value}</span>
                                    <span>/</span>
                                    <span>{item.max}</span>
                                </div>
                                <div className={s.scoreCardBar}>
                                    <IntakeScoreLevelBar value={item.value} max={item.max} getLevel={item.getLevel} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {commonSection}
            {showNav && (
                <div className={cn(s.navDetailList, !embedded && s.navDetailListDrawer)}>
                    <Collapse
                        defaultActiveKey={['']}
                        ghost
                        rootClassName={cn(s.collapsePanel, embedded && s.collapsePanelEmbedded)}
                    >
                        {navItems.map((item) => {
                            const answers = groupedIntakeAnswers[item.name] || [];
                            return (
                                <Collapse.Panel
                                    showArrow={false}
                                    forceRender
                                    key={item.name}
                                    header={
                                        <div className={s.navDetailItemHeader}>
                                            <div className={s.nihLeft}>
                                                <span className={cn(s.nihName, embedded && s.nihNameEmbedded)}>
                                                    {item.name}
                                                </span>
                                                <div
                                                    className={cn(
                                                        s.nihNameValueNum,
                                                        embedded && s.nihNameValueNumEmbedded,
                                                    )}
                                                >
                                                    <span className={s.nihNameValueNumValue}>{item.value}</span>
                                                    <span>/</span>
                                                    <span>{item.max}</span>
                                                </div>
                                                <div className={cn(s.nihNameBar, embedded && s.nihNameBarEmbedded)}>
                                                    <IntakeScoreLevelBar value={item.value} max={item.max} getLevel={item.getLevel} />
                                                </div>
                                            </div>
                                            <div className={cn(s.nihRight, embedded && s.nihRightEmbedded)}>
                                                <span>{answers.length} questions</span>
                                                <ChevronDownIcon />
                                            </div>
                                        </div>
                                    }
                                >
                                    <div
                                        className={cn(
                                            s.navDetailItemValue,
                                            embedded && s.navDetailItemValueEmbedded,
                                        )}
                                    >
                                        {answers.map((question) => (
                                            <div key={question.id} className={s.navDetailItemValueItem}>
                                                <div
                                                    className={cn(
                                                        s.navDetailItemValueQuestion,
                                                        embedded && s.navDetailItemValueQuestionEmbedded,
                                                    )}
                                                >
                                                    {question.fullQuestionName}
                                                </div>
                                                <div
                                                    className={cn(
                                                        s.navDetailItemValueAnswer,
                                                        embedded && s.navDetailItemValueAnswerEmbedded,
                                                    )}
                                                >
                                                    <AnswerLabel
                                                        level={question.score || 0}
                                                        text={question.answer || '-'}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Collapse.Panel>
                            );
                        })}
                    </Collapse>
                </div>
            )}
        </>
    );
};

export default SpecificIntakeFormAssessmentBlock;
